import React, { useState } from 'react';
import axios from 'axios';
import {
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
	Box,
	Button,
	FormControl,
	FormLabel,
	Heading,
	Stack,
	Switch,
	Tooltip,
} from '@chakra-ui/react';

import { getAge } from '../app-data/date';
import { Personal } from './profile/personal';
import { Spouse } from './profile/spouse';
import { Sponsor } from './profile/sponsor';
import { Sponsored } from './profile/sponsored';
import { EE } from './profile/ee';
import { Education } from './profile/education';
import { Experience } from './profile/experience';
import { RadioGroup } from './elements/RadioGroup';
import { extendedServices, options } from './data';

const consTypes = {
	immi: 'Immigration',
	sponsorship: 'Sponsorship',
};

export const Profile = ({ user, setUser, setWake, isSubmitDisabled }) => {
	const [showTypes, setShowTypes] = useState(false);
	const [submitTxt, setSubmitTxt] = useState('Save changes');

	const consType = user.cons_type || 'immi';
	const hasSpouse =
		['official', 'commonLaw'].includes(user.marital_status) &&
		consType !== 'sponsorship';
	const isSponsorship = consType === 'sponsorship';
	const isExt = extendedServices.includes(user.service);

	const handleUpdate = () => {
		setSubmitTxt('Sending...');
		axios({
			method: 'put',
			url: `/users`,
			data: user,
			...options(),
		})
			.then(() => {
				setSubmitTxt('Save changes');
				setWake(new Date());
			})
			.catch(console.log);
	};

	const personal = { user, setUser, handleUpdate };

	const educationSubState = user.structured.education;
	const setEducation = education => {
		const structured = { ...user.structured, education };
		setUser({ ...user, structured });
	};
	const education = {
		state: educationSubState,
		setter: setEducation,
		onBlur: handleUpdate,
	};

	const experienceSubState = user.structured.experience;
	const setExperience = experience => {
		const structured = { ...user.structured, experience };
		setUser({ ...user, structured });
	};
	const experience = {
		state: experienceSubState,
		setter: setExperience,
		onBlur: handleUpdate,
	};

	const spouseSubState = {
		...user.structured.spouse,
		age: getAge(user.structured.spouse.birth_date),
	};
	const setSpouse = spouse => {
		const structured = { ...user.structured, spouse };
		setUser({ ...user, structured });
	};
	const spouse = {
		state: spouseSubState,
		setter: setSpouse,
		onBlur: handleUpdate,
	};

	const sponsorSubState = user.structured.sponsor;
	const setSponsor = sponsor => {
		const structured = { ...user.structured, sponsor };
		setUser({ ...user, structured });
	};
	const sponsor = {
		state: sponsorSubState,
		setter: setSponsor,
		onBlur: handleUpdate,
	};

	const sponsoredSubState = user.structured.sponsored;
	const setSponsored = sponsored => {
		const structured = { ...user.structured, sponsored };
		setUser({ ...user, structured });
	};
	const sponsored = {
		state: sponsoredSubState,
		setter: setSponsored,
		onBlur: handleUpdate,
	};

	const extSubState = user.structured.ext;
	const setExt = ext => {
		const structured = { ...user.structured, ext };
		setUser({ ...user, structured });
	};
	const ext = {
		state: extSubState,
		setter: setExt,
		onBlur: handleUpdate,
	};

	return (
		<Box>
			<Stack direction={['column', 'row']} align="center">
				<Box w={['100%', '25%']}>
					<FormControl display="flex" alignItems="center" py={8}>
									<Tooltip
										label="Показать выбор типа консультации"
										placement="top-start"
									>
						<FormLabel htmlFor="showTypes" mb="0">
							Change for Spouse Sponsorship form
						</FormLabel>
						</Tooltip>
						<Switch
							id="showTypes"
							value={showTypes}
							onChange={e => setShowTypes(e.target.checked)}
						/>
					</FormControl>
				</Box>
				<Box w={['100%', '75%']}>
					{showTypes && (
						<RadioGroup
							name="consType"
							options={consTypes}
							defaultValue={user.cons_type}
							onChange={cons_type => setUser({ ...user, cons_type })}
						/>
					)}
				</Box>
			</Stack>
			<Box>
				<Accordion>
					{!isSponsorship && (
						<AccordionItem>
							<h2>
								<AccordionButton>
									<Tooltip label="Личная информация" placement="top-start">
										<Heading fontSize="lg" flex="1" textAlign="left">
											Personal Information
										</Heading>
									</Tooltip>
									<AccordionIcon />
								</AccordionButton>
							</h2>
							<AccordionPanel pb={4}>
								<Personal {...personal} />
							</AccordionPanel>
						</AccordionItem>
					)}

					{!isSponsorship && (
						<AccordionItem>
							<h2>
								<AccordionButton>
									<Tooltip
										label="Послешкольное образование (все вузы)"
										placement="top-start"
									>
										<Heading fontSize="lg" flex="1" textAlign="left">
											Post-secondary education
										</Heading>
									</Tooltip>
									<AccordionIcon />
								</AccordionButton>
							</h2>
							<AccordionPanel pb={4}>
								<Education {...education} />
							</AccordionPanel>
						</AccordionItem>
					)}

					{!isSponsorship && (
						<AccordionItem>
							<h2>
								<AccordionButton>
									<Tooltip
										label="Опыт работы за последние 10 лет"
										placement="top-start"
									>
										<Heading fontSize="lg" flex="1" textAlign="left">
											Work Experience
										</Heading>
									</Tooltip>
									<AccordionIcon />
								</AccordionButton>
							</h2>
							<AccordionPanel pb={4}>
								<Experience {...experience} />
							</AccordionPanel>
						</AccordionItem>
					)}

					{hasSpouse && (
						<AccordionItem>
							<h2>
								<AccordionButton>
									<Tooltip label="Данные о супруге" placement="top-start">
										<Heading fontSize="lg" flex="1" textAlign="left">
											Spouse Information
										</Heading>
									</Tooltip>
									<AccordionIcon />
								</AccordionButton>
							</h2>
							<AccordionPanel pb={4}>
								<Spouse {...spouse} />
							</AccordionPanel>
						</AccordionItem>
					)}

					{isSponsorship && (
						<AccordionItem>
							<h2>
								<AccordionButton>
									<Tooltip
										label="Данные о спонсируемом супруге"
										placement="top-start"
									>
										<Heading fontSize="lg" flex="1" textAlign="left">
											Sponsored spouse information
										</Heading>
									</Tooltip>
									<AccordionIcon />
								</AccordionButton>
							</h2>
							<AccordionPanel pb={4}>
								<Sponsored {...sponsored} />
							</AccordionPanel>
						</AccordionItem>
					)}

					{isSponsorship && (
						<AccordionItem>
							<h2>
								<AccordionButton>
									<Tooltip
										label="Данные о спонсоре (канадском супруге)"
										placement="top-start"
									>
										<Heading fontSize="lg" flex="1" textAlign="left">
											Sponsor information
										</Heading>
									</Tooltip>
									<AccordionIcon />
								</AccordionButton>
							</h2>
							<AccordionPanel pb={4}>
								<Sponsor {...sponsor} />
							</AccordionPanel>
						</AccordionItem>
					)}

					{isExt && (
						<AccordionItem>
							<h2>
								<AccordionButton>
									<Tooltip
										label="Данные для процесса Express Entry"
										placement="top-start"
									>
										<Heading fontSize="lg" flex="1" textAlign="left">
											Express Entry infomation
										</Heading>
									</Tooltip>
									<AccordionIcon />
								</AccordionButton>
							</h2>
							<AccordionPanel pb={4}>
								<EE {...ext} />
							</AccordionPanel>
						</AccordionItem>
					)}
				</Accordion>
			</Box>
			<Box mt={6}>
				<Tooltip label="Сохранить изменения" placement="top-start">
					<Button
						d="block"
						w="100%"
						colorScheme="blue"
						isDisabled={isSubmitDisabled}
						onClick={handleUpdate}
					>
						{submitTxt}
					</Button>
				</Tooltip>
			</Box>
		</Box>
	);
};
